// ── useStorytellers ──
// Lists every storyteller_profile the current user has access to (multiple
// for captains who manage several storytellers, usually one for crew/storyteller),
// and tracks which one is currently active.
//
// The active storyteller id is persisted to:
//   1. profiles.active_storyteller_id  (cross-device, source of truth)
//   2. localStorage 'active_storyteller_<userId>'  (instant on page load)
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext.jsx';
import { supabase } from '@/services/supabase.js';

export function useStorytellers() {
  const { profile, user } = useAuth();
  const [storytellers, setStorytellers] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load all storyteller_profiles this user has access to
  const loadStorytellers = useCallback(async () => {
    if (!profile?.id || !user?.id) {
      setStorytellers([]);
      setLoading(false);
      return;
    }

    try {
      let rows = [];
      const seen = new Set();

      // 1. Storyteller users: include all their OWN storyteller_profiles
      //    (one per volume). Listed first so volumes group naturally.
      if (profile.role === 'storyteller') {
        const { data } = await supabase
          .from('storyteller_profiles')
          .select('*')
          .eq('auth_user_id', user.id)
          .order('volume_number', { ascending: true });
        for (const sp of data || []) {
          if (!seen.has(sp.id)) {
            seen.add(sp.id);
            rows.push(sp);
          }
        }
      }

      // 2. ANY user: walk family_links for active memberships. This catches
      //    crew-on-someone-else's-project and captain-on-multiple-projects.
      //    Critical for the multi-role case: e.g. a storyteller user who's
      //    been added as crew to another captain's project.
      //
      //    We deliberately do this as TWO queries (not an embedded join)
      //    because Supabase's RLS on storyteller_profiles + the recently
      //    added storyteller_profile_id FK can cause the embedded join to
      //    silently return null for projects the user joined via crew —
      //    which would drop them from the switcher list.
      const { data: links } = await supabase
        .from('family_links')
        .select('storyteller_profile_id, invited_by, role')
        .eq('family_user_id', profile.id)
        .eq('status', 'active');
      const linkProfileIds = [...new Set((links || [])
        .map(l => l.storyteller_profile_id)
        .filter(Boolean)
        .filter(id => !seen.has(id)))];
      if (linkProfileIds.length > 0) {
        const { data: linkedProfiles } = await supabase
          .from('storyteller_profiles')
          .select('*')
          .in('id', linkProfileIds);
        const byId = new Map((linkedProfiles || []).map(p => [p.id, p]));
        for (const link of links || []) {
          const sp = byId.get(link.storyteller_profile_id);
          if (!sp || seen.has(sp.id)) continue;
          seen.add(sp.id);
          rows.push({
            ...sp,
            _captainId: link.invited_by,
            _isCaptain: link.role === 'captain',
          });
        }
      }

      // 3. Non-storyteller users: also pull any storyteller_profiles they
      //    CREATED (legacy path — before backfill, captains didn't always
      //    have a family_links row pointing at their own project).
      if (profile.role !== 'storyteller') {
        const { data: ownedProfiles } = await supabase
          .from('storyteller_profiles')
          .select('*')
          .eq('created_by', profile.id);
        for (const sp of ownedProfiles || []) {
          if (!seen.has(sp.id)) {
            seen.add(sp.id);
            rows.push({ ...sp, _captainId: profile.id, _isCaptain: true });
          }
        }
      }

      // Sort: same person's volumes appear together (by parent then volume)
      rows.sort(function (a, b) {
        var aName = (a.first_name || '') + (a.last_name || '');
        var bName = (b.first_name || '') + (b.last_name || '');
        if (aName !== bName) return aName.localeCompare(bName);
        return (a.volume_number || 1) - (b.volume_number || 1);
      });

      setStorytellers(rows);

      // Determine active storyteller: profile.active_storyteller_id, then
      // localStorage, then the first one. Validate that the active id is
      // actually in the list (in case it was removed).
      const lsKey = 'active_storyteller_' + user.id;
      const lsValue = typeof localStorage !== 'undefined' ? localStorage.getItem(lsKey) : null;
      const candidate = profile.active_storyteller_id || lsValue;
      const valid = rows.find(r => r.id === candidate);
      const newActive = valid ? valid.id : (rows[0]?.id || null);
      setActiveId(newActive);

      // Sync localStorage if it diverged
      if (newActive && newActive !== lsValue) {
        try { localStorage.setItem(lsKey, newActive); } catch {}
      }
    } catch (err) {
      console.error('[useStorytellers] Load failed:', err.message);
    } finally {
      setLoading(false);
    }
  }, [profile?.id, profile?.role, profile?.active_storyteller_id, user?.id]);

  useEffect(() => { loadStorytellers(); }, [loadStorytellers]);

  // Switch to a different storyteller. Persists to profiles + localStorage.
  const switchStoryteller = useCallback(async (storytellerProfileId) => {
    if (!profile?.id || !user?.id) return;
    if (!storytellers.find(s => s.id === storytellerProfileId)) {
      console.warn('[useStorytellers] Cannot switch — id not in list');
      return;
    }
    setActiveId(storytellerProfileId);
    try {
      localStorage.setItem('active_storyteller_' + user.id, storytellerProfileId);
    } catch {}
    try {
      await supabase.from('profiles').update({ active_storyteller_id: storytellerProfileId }).eq('id', profile.id);
    } catch (err) {
      console.warn('[useStorytellers] Failed to save active id to profile:', err.message);
    }
  }, [profile?.id, user?.id, storytellers]);

  const active = storytellers.find(s => s.id === activeId) || null;

  return {
    storytellers,
    active,
    activeId,
    loading,
    switchStoryteller,
    refresh: loadStorytellers,
  };
}
